import React, { useState } from 'react';
import {
  ButtonTertiary,
  cssColorValue,
  desktopBallad,
  desktopCelloCanon,
  IconArrowTopRight,
  IconChevronDown,
  IconChevronUp,
  mobileBallad,
  mobileCello,
  screenXsMin,
  semanticColors,
  spacer20,
  spacer4,
} from '@spotify-internal/encore-web/core';
import styled from 'styled-components';

import { MastheadLinkList, MastheadLinkOrLinkListArray } from 'lib/sanity/types';
import { isMastheadLinkList } from 'utils/type-guards';
import useDropdown, { DropdownContext } from 'components/Masthead/Header/hooks/useDropdown';
import { useTranslation } from 'providers/translations/TranslationProvider';
import { PopoverLink } from 'components/Masthead/Header/types';
import {
  SideMenuNavigationItem,
  SideMenuNavigationLink,
  SideMenuNavigationList,
} from 'components/Masthead/Header/SideMenu/Shared';

// The font size drops at `screenXsMin`, so we need to translate the icons to keep the alignment correct
const StyledIconChevronDown = styled(IconChevronDown)`
  @media (max-width: ${screenXsMin}) {
    transform: translateY(2px);
  }
`;

const StyledIconChevronUp = styled(IconChevronUp)`
  @media (max-width: ${screenXsMin}) {
    transform: translateY(4px);
  }
`;

const DropdownButton = styled(ButtonTertiary)`
  && {
    border-radius: 0;
    color: ${cssColorValue(semanticColors.textBase)};
    padding: 0;
    min-block-size: auto;
    ${desktopCelloCanon};

    &&:active,
    &&:hover,
    &&:focus {
      color: ${cssColorValue(semanticColors.textBase)};
      transform: none;
    }

    &:focus {
      outline: 5px auto -webkit-focus-ring-color;
      outline-offset: ${spacer4};
    }

    &::after {
      display: none;
    }

    @media (max-width: ${screenXsMin}) {
      ${mobileCello}
    }
  }
`;

const DropdownList = styled(SideMenuNavigationList)`
  margin-bottom: 0;
  margin-inline-start: ${spacer20};
  margin-top: ${spacer20};
`;

const DropdownLink = styled(ButtonTertiary)`
  color: ${cssColorValue(semanticColors.textSubdued)};
  cursor: pointer;
  justify-content: flex-start;
  padding: 0;
  min-block-size: auto;
  ${desktopBallad};

  &:active,
  &:hover,
  &:focus {
    color: ${cssColorValue(semanticColors.textSubdued)};
    transform: none;
  }

  &::after {
    bottom: 0;
    inline-size: 100%;
  }

  [dir='rtl'] & * {
    direction: rtl;
    & > svg {
      transform: rotateY(180deg);
    }
  }

  & > * {
    margin-inline-start: ${spacer4};
    transform: translateY(1px);
  }

  @media (max-width: ${screenXsMin}) {
    ${mobileBallad}
  }
`;

type SideMenuDropdownProps = {
  linkListEntry: MastheadLinkList;
  closeBackdrop: () => void;
};

const SideMenuDropdown = ({ linkListEntry, closeBackdrop }: SideMenuDropdownProps) => {
  const { handlers, open } = useDropdown(linkListEntry.label, true);
  const { t } = useTranslation();

  const links = (linkListEntry.entries || []).map<PopoverLink>(linkListItem => {
    const link = linkListItem.link;
    return {
      href: link.href,
      linkText: link.title,
      level: linkListItem?.nestingLevel,
      target: link.target,
    };
  });

  return (
    <>
      <DropdownButton
        href=""
        iconTrailing={() =>
          open ? (
            <StyledIconChevronUp size="medium" aria-label={t('navbar_dropdown_menu_close')} />
          ) : (
            <StyledIconChevronDown size="medium" aria-label={t('navbar_dropdown_menu_open')} />
          )
        }
        onKeyDown={handlers.handleOnKeyDown}
        onMouseDown={handlers.handleOnMouseDown}
      >
        {linkListEntry.label}
      </DropdownButton>
      {open && (
        <DropdownList $variant="default">
          {links.map(({ linkText, target, href, level, ...rest }, i) => (
            <li key={i}>
              <DropdownLink
                onClick={closeBackdrop}
                target={target}
                href={href}
                iconTrailing={target === '_blank' ? () => <IconArrowTopRight size="small" /> : undefined}
                {...rest}
              >
                {linkText}
              </DropdownLink>
            </li>
          ))}
        </DropdownList>
      )}
    </>
  );
};

type Props = {
  primaryLinks?: MastheadLinkOrLinkListArray;
  closeBackdrop: () => void;
};

export const SideMenuPrimaryLinks = ({ primaryLinks, closeBackdrop }: Props) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  return (
    <>
      {primaryLinks?.map((item, i) => {
        return (
          <SideMenuNavigationItem key={i}>
            {isMastheadLinkList(item) ? (
              <DropdownContext.Provider value={{ activeDropdown, setActiveDropdown }}>
                <SideMenuDropdown linkListEntry={item} closeBackdrop={closeBackdrop} />
              </DropdownContext.Provider>
            ) : (
              <SideMenuNavigationLink href={item.href} target={item.target} onClick={closeBackdrop}>
                {item.title}
              </SideMenuNavigationLink>
            )}
          </SideMenuNavigationItem>
        );
      })}
    </>
  );
};
